import { Alert, Button, ThemeProvider, Typography } from '@material-tailwind/react';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { ProducerLocation } from '../../types';
import { renderLocation } from '../lib/formatters';
import { joinRestaurantTrackingRoom, leaveRestaurantTrackingRoom } from '../lib/socket';
import useUser from '../store/user';
import ChangeAddressModal from './Modals/ChangeAddressModal';

const theme = {
	input: {
		styles: {
			base: {
				container: {
					minWidth: 'min-w-[50px]',
				},
			},
		},
	},
};

interface Props {
	producerLocations?: ProducerLocation[] | null;
	toggleFoodTruckOperation: (producerLocation: ProducerLocation) => void;
	onAddressChanged: (producerLocation: ProducerLocation, newLocation: Object) => void;
}

const RestaurantConfigurationTable = ({ producerLocations, toggleFoodTruckOperation, onAddressChanged }: Props) => {
	console.log('producerLocations1', producerLocations);

	const [changeAddressModalOpen, setChangeAddressModalOpen] = useState(false);
	const [selectedProducerLocation, setSelectedProducerLocation] = useState();
	const {user} = useUser();

	useEffect(() => {
		let { producerKey = null, partnerKey } = user || {};
		if (partnerKey === 'wonder' && (!producerKey || producerKey === '')) {
			producerKey = 'wonder'
		}

		if (!producerKey) {
			return;
		}

		joinRestaurantTrackingRoom(producerKey);

		return () => {
			leaveRestaurantTrackingRoom(producerKey!);
		};
	}, [user]);

	const toggleOpen = () => {
		setChangeAddressModalOpen(!changeAddressModalOpen);
	};

	const headers = [
		{
			label: 'Name',
			width: 'w-[25%]',
		},
		{
			label: 'Location',
		},
	].filter(Boolean);

	const onAddressChangedSubmitSuccess = (newLocation) => {
		if (!selectedProducerLocation) {
			return;
		}
		onAddressChanged(selectedProducerLocation, newLocation);
	};

	return (
		<ThemeProvider value={theme}>
			<>
				<div className="flex justify-between items-center p-3 h-20">
					<h5 className="text-xl font-semibold">Restaurants</h5>
				</div>
				<div className="flex-col max-h-[calc(100vh-10rem)] items-center">
					<table className="border mt-4 w-full text-left">
						<TableHeaders headers={headers} />
						<TableRows
							rows={producerLocations}
							headers={headers}
							toggleFoodTruckOperation={toggleFoodTruckOperation}
							setChangeAddressModalOpen={setChangeAddressModalOpen}
							setSelectedProducerLocation={setSelectedProducerLocation}
						/>
					</table>
				</div>
				<ChangeAddressModal
					toggleOpen={toggleOpen}
					producerLocation={selectedProducerLocation}
					onSubmitSuccess={onAddressChangedSubmitSuccess}
					changeAddressModalOpen={changeAddressModalOpen}
				/>
			</>
		</ThemeProvider>
	);
};

const TableHeaders = ({ headers, sortProps = {} }) => {
	return (
		<thead>
			<tr>
				{headers.map((head) => (
					<th
						key={head.label}
						className={`cursor-pointer border-y border-blue-gray-100 bg-blue-gray-50/50 p-4 transition-colors hover:bg-blue-gray-50 ${
							head.width || ''
						}`}
						colSpan={head.colSpan || 1}
					>
						<div className="flex items-center justify-between gap-2 font-normal leading-none">
							<div className="w-full gap-6 opacity-70">
								<Typography variant="small" color="blue-gray" className="font-[420]">
									{head.label}
								</Typography>
							</div>
						</div>
					</th>
				))}
			</tr>
		</thead>
	);
};

const TableRows = ({
	rows,
	headers,
	toggleFoodTruckOperation,
	setChangeAddressModalOpen,
	setSelectedProducerLocation,
}) => {
	console.log('rows', rows);
	return (
		<tbody>
			{rows.length === 0 && (
				<tr className="border-b-0">
					<td colSpan={headers.map((h) => h.colSpan || 1).reduce((a, b) => a + b)} className="p-8 py-4">
						<Alert color="orange" variant="ghost">
							Loading...
						</Alert>
					</td>
				</tr>
			)}
			{rows
				.sort((a, b) => (b.isFoodTruck ? 1 : 0) - (a.isFoodTruck ? 1 : 0))
				.map((producerLocation, index) => {
					const { producerLocationKey, producerLocationName, isFoodTruck, isFoodTruckOperationPaused } =
						producerLocation;

					const isLast = index === rows.length - 1;
					const classes = isLast ? 'p-4' : 'p-4 border-b border-blue-gray-50';
					const cellClasses = 'flex flex-row items-center gap-3';

					return (
						<tr key={producerLocationKey + `${index}`} className={'cursor-pointer hover:bg-gray-100'}>
							<td className={classes}>
								<div className={cellClasses}>
									<Typography variant="small" color="blue-gray" className="font-normal">
										{`${isFoodTruck ? '🚚' : ''} ${producerLocationName}`}
									</Typography>
									{isFoodTruck && (
										<Button
											variant="gradient"
											size="sm"
											color={isFoodTruckOperationPaused ? 'green' : 'orange'}
											onClick={() => {
												toggleFoodTruckOperation(producerLocation).then((res) => {
													if (!res.success) {
														toast.error(res.message);
													} else {
														toast.success(
															`Food truck ${
																isFoodTruckOperationPaused ? 'Resumed' : 'Paused'
															} successfully!`
														);
													}
												});
											}}
										>
											{`${isFoodTruckOperationPaused ? 'Resume' : 'Pause'} Operations`}
										</Button>
									)}
								</div>
							</td>
							<td className={classes}>
								<div className={cellClasses}>
									<div className="flex flex-col gap-y-4 justify-start">
										<div className={cellClasses}>
											<Typography variant="small" color="blue-gray" className="font-normal">
												<b>Address:&nbsp;</b>
												{renderLocation(producerLocation)}
											</Typography>
											{isFoodTruck && (
												<Button
													variant="gradient"
													size="sm"
													color="blue"
													onClick={(e) => {
														e.preventDefault();
														e.stopPropagation();
														setSelectedProducerLocation(producerLocation);
														setChangeAddressModalOpen(true);
													}}
												>
													Change
												</Button>
											)}
										</div>
										{producerLocation.location?.crossStreets && (
											<Typography
												variant="small"
												color="blue-gray"
												className="font-normal text-wrap"
											>
												<b>Cross Streets:&nbsp;</b>
												{producerLocation.location?.crossStreets}
											</Typography>
										)}
										{producerLocation.location?.crossStreets && (
											<Typography
												variant="small"
												color="blue-gray"
												className="font-normal text-wrap"
											>
												<b>Special Instructions:&nbsp;</b>
												{producerLocation.location?.instructions?.english}
											</Typography>
										)}
									</div>
								</div>
							</td>
						</tr>
					);
				})}
		</tbody>
	);
};

export default RestaurantConfigurationTable;
